import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { COLORS } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { userAPI, tradingAPI, notificationsAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import WalletConnectModal from '../components/wallet/WalletConnectModal';
import {
  User, Settings, Shield, Wallet, LogOut, ChevronRight, Eye, EyeOff,
  Edit3, Phone, Mail, Check, X, Globe, TrendingUp, TrendingDown,
  Copy, CheckCircle, Trash2, Plus, ExternalLink, AlertCircle, MessageSquare, Bell
} from 'lucide-react';

const ProfilePage = () => {
  const { user, logout, refreshUser } = useAuth();
  const { connectedWallets, connect, disconnect, isConnected } = useWeb3();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  
  // Profile data
  const [profile, setProfile] = useState(null);
  const [balance, setBalance] = useState(null);
  const [stats, setStats] = useState({
    totalPnL: 0, winRate: 0, totalTrades: 0,
    winTrades: 0, lossTrades: 0, openTrades: 0, closedTrades: 0,
  });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [showBalance, setShowBalance] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [copied, setCopied] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  
  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ name: '', phone: '' });
  const [saving, setSaving] = useState(false);
  
  // Settings modal
  const [showSettings, setShowSettings] = useState(false);
  
  // Wallet modal
  const [showWalletModal, setShowWalletModal] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load notifications count
  useEffect(() => {
    const loadUnreadCount = async () => {
      try {
        const res = await notificationsAPI.getUnreadCount();
        setUnreadNotifications(res.data?.count || 0);
      } catch (err) {}
    };
    loadUnreadCount();
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, balanceRes, tradesRes] = await Promise.all([
        userAPI.getProfile().catch(() => ({ data: null })),
        userAPI.getBalance(),
        tradingAPI.getTrades(),
      ]);

      if (profileRes.data) {
        setProfile(profileRes.data);
        setEditData({
          name: profileRes.data.name || '',
          phone: profileRes.data.phone || '',
        });
      }
      setBalance(balanceRes.data);
      
      const trades = tradesRes.data;
      const openTrades = trades.filter(t => t.status === 'open');
      const closedTrades = trades.filter(t => t.status === 'closed');
      const winTrades = closedTrades.filter(t => t.pnl > 0);
      const lossTrades = closedTrades.filter(t => t.pnl < 0);
      
      setStats({
        totalPnL: trades.reduce((sum, t) => sum + (t.pnl || 0), 0),
        winRate: closedTrades.length > 0 ? (winTrades.length / closedTrades.length * 100) : 0,
        totalTrades: trades.length,
        winTrades: winTrades.length,
        lossTrades: lossTrades.length,
        openTrades: openTrades.length,
        closedTrades: closedTrades.length,
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await userAPI.updateProfile({
        name: editData.name,
        phone: editData.phone,
      });
      setProfile(res.data);
      setEditMode(false);
      if (refreshUser) refreshUser();
    } catch (err) {
      console.error('Failed to save profile:', err);
      alert('Ошибка сохранения профиля');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const formatCurrency = (value) => {
    if (!showBalance) return '••••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD', minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const displayName = profile?.name || user?.email?.split('@')[0] || 'User';

  // Settings Modal
  const SettingsModal = () => (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center',
      zIndex: 10000, padding: isMobile ? 0 : '20px',
    }}>
      <div style={{
        backgroundColor: COLORS.card,
        borderRadius: isMobile ? '20px 20px 0 0' : '20px',
        width: '100%', maxWidth: isMobile ? '100%' : '400px',
        padding: '24px', maxHeight: isMobile ? '80vh' : 'auto', overflow: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h3 style={{ color: COLORS.text, fontSize: '20px', fontWeight: '700', margin: 0 }}>
            {t('settings')}
          </h3>
          <button onClick={() => setShowSettings(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X size={24} color={COLORS.textSecondary} />
          </button>
        </div>

        {/* Language Selection */}
        <div>
          <div style={{ color: COLORS.textSecondary, fontSize: '13px', marginBottom: '12px', fontWeight: '600' }}>
            {t('language')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {Object.entries(LANGUAGES).map(([key, langData]) => (
              <button
                key={key}
                onClick={() => setLanguage(key)}
                style={{
                  padding: '14px 16px',
                  backgroundColor: language === key ? `${COLORS.primary}20` : COLORS.background,
                  border: `2px solid ${language === key ? COLORS.primary : COLORS.border}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '12px',
                  fontSize: '14px', fontWeight: '500', color: COLORS.text,
                }}
              >
                <span style={{ fontSize: '20px' }}>{langData.flag}</span>
                {langData.label}
                {language === key && <Check size={18} color={COLORS.primary} style={{ marginLeft: 'auto' }} />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <div style={{
          width: '40px', height: '40px', border: `3px solid ${COLORS.border}`,
          borderTop: `3px solid ${COLORS.primary}`, borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // ==================== MOBILE LAYOUT ====================
  if (isMobile) {
    return (
      <div style={{ paddingBottom: '100px', paddingTop: '12px', backgroundColor: COLORS.background, minHeight: '100vh' }}>
        {/* Profile Card with ExDex header */}
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.card} 0%, #0f1114 100%)`,
          borderRadius: '20px', padding: '24px 20px', marginBottom: '16px',
          border: `1px solid ${COLORS.border}`,
        }}>
          {/* Profile section header inside card */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 10, 
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: `1px solid ${COLORS.border}`,
          }}>
            <User size={22} color={COLORS.primary} />
            <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 700 }}>{t('profile')}</span>
          </div>

          {/* Avatar & Name */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{
              width: '70px', height: '70px', borderRadius: '50%',
              background: `linear-gradient(135deg, ${COLORS.primary} 0%, #d4a50a 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '28px', fontWeight: '700', color: '#000',
              flexShrink: 0,
            }}>
              {displayName.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {editMode ? (
                <input
                  type="text"
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  placeholder={t('name')}
                  style={{
                    width: '100%', padding: '10px 12px', backgroundColor: COLORS.background,
                    border: `1px solid ${COLORS.border}`, borderRadius: '8px',
                    color: COLORS.text, fontSize: '16px', fontWeight: '600', marginBottom: '8px',
                    boxSizing: 'border-box',
                  }}
                />
              ) : (
                <div style={{ fontSize: '20px', fontWeight: '700', color: COLORS.text, marginBottom: '4px' }}>
                  {displayName}
                </div>
              )}
              <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>{user?.email}</div>
              {editMode ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                  <Phone size={14} color={COLORS.textSecondary} style={{ flexShrink: 0 }} />
                  <input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    placeholder={t('phone')}
                    style={{
                      flex: 1, padding: '8px 10px', backgroundColor: COLORS.background,
                      border: `1px solid ${COLORS.border}`, borderRadius: '6px',
                      color: COLORS.text, fontSize: '13px', minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              ) : profile?.phone && (
                <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px' }}>
                  📱 {profile.phone}
                </div>
              )}
            </div>
            {editMode ? (
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button onClick={handleSaveProfile} disabled={saving} style={{
                  padding: '10px', backgroundColor: COLORS.success, border: 'none',
                  borderRadius: '10px', cursor: 'pointer',
                }}>
                  <Check size={20} color="#fff" />
                </button>
                <button onClick={() => { setEditMode(false); setEditData({ name: profile?.name || '', phone: profile?.phone || '' }); }} style={{
                  padding: '10px', backgroundColor: COLORS.border, border: 'none',
                  borderRadius: '10px', cursor: 'pointer',
                }}>
                  <X size={20} color={COLORS.text} />
                </button>
              </div>
            ) : (
              <button onClick={() => setEditMode(true)} style={{
                padding: '10px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`,
                borderRadius: '10px', cursor: 'pointer',
              }}>
                <Edit3 size={18} color={COLORS.text} />
              </button>
            )}
          </div>

          {/* Balance */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ color: COLORS.textSecondary, fontSize: '13px' }}>{t('totalBalance')}</span>
              <button onClick={() => setShowBalance(!showBalance)} style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer' }}>
                {showBalance ? <Eye size={14} color={COLORS.textSecondary} /> : <EyeOff size={14} color={COLORS.textSecondary} />}
              </button>
            </div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: COLORS.text }}>
              {formatCurrency(balance?.exchange_balance)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
              {stats.totalPnL >= 0 ? <TrendingUp size={14} color={COLORS.success} /> : <TrendingDown size={14} color={COLORS.danger} />}
              <span style={{ color: stats.totalPnL >= 0 ? COLORS.success : COLORS.danger, fontSize: '14px', fontWeight: '600' }}>
                {showBalance ? `${stats.totalPnL >= 0 ? '+' : ''}$${stats.totalPnL.toFixed(2)}` : '••••'}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
            {[
              { label: t('winRate'), value: `${stats.winRate.toFixed(0)}%`, color: COLORS.primary },
              { label: t('profitable'), value: stats.winTrades, color: COLORS.success },
              { label: t('losing'), value: stats.lossTrades, color: COLORS.danger },
              { label: t('trades'), value: stats.totalTrades, color: COLORS.text },
            ].map((stat, i) => (
              <div key={i} style={{ textAlign: 'center', padding: '10px 6px', backgroundColor: `${COLORS.background}80`, borderRadius: '10px' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: '10px', color: COLORS.textSecondary, marginTop: '2px' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Connected Wallets */}
        <div style={{ backgroundColor: COLORS.card, borderRadius: '16px', padding: '16px', marginBottom: '16px', border: `1px solid ${COLORS.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600' }}>{t('connectedWallets')}</span>
            <button 
              type="button"
              onClick={() => { console.log('Connect wallet clicked'); setShowWalletModal(true); }} 
              style={{
                padding: '8px 14px', backgroundColor: COLORS.primary, color: '#000', border: 'none',
                borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '6px',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation',
              }}>
              <Plus size={14} /> {t('connectWallet')}
            </button>
          </div>
          {connectedWallets.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: COLORS.textSecondary, fontSize: '13px' }}>
              {t('noWallets')}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {connectedWallets.map((wallet, idx) => (
                <div key={idx} style={{
                  backgroundColor: COLORS.background, borderRadius: '12px', padding: '14px',
                  display: 'flex', alignItems: 'center', gap: '12px', border: `1px solid ${COLORS.border}`,
                }}>
                  <div style={{
                    width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${COLORS.success}20`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <Wallet size={20} color={COLORS.success} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.text, marginBottom: '2px' }}>
                      {formatAddress(wallet.address)}
                    </div>
                    <div style={{ fontSize: '11px', color: COLORS.textSecondary }}>{wallet.walletType || 'Ethereum'}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => handleCopy(wallet.address)} style={{
                      padding: '8px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`,
                      borderRadius: '8px', cursor: 'pointer',
                    }}>
                      {copied ? <CheckCircle size={16} color={COLORS.success} /> : <Copy size={16} color={COLORS.textSecondary} />}
                    </button>
                    <button 
                      onClick={() => disconnect(wallet.address)} 
                      data-testid={`delete-wallet-${idx}`}
                      style={{
                        padding: '8px', backgroundColor: `${COLORS.danger}20`, border: 'none',
                        borderRadius: '8px', cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={16} color={COLORS.danger} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Menu */}
        <div style={{ backgroundColor: COLORS.card, borderRadius: '16px', overflow: 'hidden', marginBottom: '16px', border: `1px solid ${COLORS.border}` }}>
          {[
            { id: 'settings', icon: Settings, label: t('settings'), color: COLORS.primary, onClick: () => setShowSettings(true) },
            { id: 'security', icon: Shield, label: t('security'), color: COLORS.success, onClick: () => navigate('/security') },
            { id: 'support', icon: MessageSquare, label: t('support'), color: COLORS.warning, onClick: () => navigate('/support') },
          ].map((item, idx, arr) => (
            <button
              key={item.id}
              onClick={item.onClick}
              style={{
                width: '100%', padding: '18px 16px', backgroundColor: 'transparent', border: 'none',
                borderBottom: idx < arr.length - 1 ? `1px solid ${COLORS.border}` : 'none',
                display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer',
              }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '12px', backgroundColor: `${item.color}15`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <item.icon size={20} color={item.color} />
              </div>
              <span style={{ flex: 1, textAlign: 'left', fontSize: '15px', fontWeight: '500', color: COLORS.text }}>{item.label}</span>
              <ChevronRight size={20} color={COLORS.textSecondary} />
            </button>
          ))}
        </div>

        {/* Logout */}
        <button onClick={handleLogout} style={{
          width: '100%', padding: '16px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.danger}30`,
          borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer',
        }}>
          <LogOut size={20} color={COLORS.danger} />
          <span style={{ fontSize: '15px', fontWeight: '600', color: COLORS.danger }}>{t('logout')}</span>
        </button>

        {/* Settings Modal */}
        {showSettings && <SettingsModal />}
        
        {/* Wallet Connect Modal */}
        <WalletConnectModal 
          isOpen={showWalletModal} 
          onClose={() => setShowWalletModal(false)}
          onConnect={connect}
          connectedWallets={connectedWallets}
        />
      </div>
    );
  }

  // ==================== DESKTOP LAYOUT ====================
  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* Main Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '24px' }}>
        {/* Left Column */}
        <div>
          {/* Profile Card */}
          <div style={{
            background: `linear-gradient(135deg, ${COLORS.card} 0%, #1a1d21 100%)`,
            border: `1px solid ${COLORS.border}`, borderRadius: '20px', padding: '32px', marginBottom: '24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '28px' }}>
              <div style={{
                width: '90px', height: '90px', borderRadius: '50%',
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, #d4a50a 100%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '36px', fontWeight: '700', color: '#000',
              }}>
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                {editMode ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <input
                      type="text"
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      placeholder={t('name')}
                      style={{
                        padding: '12px 14px', backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`, borderRadius: '10px',
                        color: COLORS.text, fontSize: '18px', fontWeight: '600',
                      }}
                    />
                    <input
                      type="tel"
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      placeholder={t('phone')}
                      style={{
                        padding: '10px 14px', backgroundColor: COLORS.background,
                        border: `1px solid ${COLORS.border}`, borderRadius: '10px',
                        color: COLORS.text, fontSize: '14px',
                      }}
                    />
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: '26px', fontWeight: '700', color: COLORS.text, marginBottom: '4px' }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: '14px', color: COLORS.textSecondary, marginBottom: '4px' }}>{user?.email}</div>
                    {profile?.phone && (
                      <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>📱 {profile.phone}</div>
                    )}
                  </>
                )}
              </div>
              {editMode ? (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleSaveProfile} disabled={saving} style={{
                    padding: '12px 20px', backgroundColor: COLORS.success, color: '#fff', border: 'none',
                    borderRadius: '10px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px',
                  }}>
                    <Check size={18} /> {t('save')}
                  </button>
                  <button onClick={() => { setEditMode(false); setEditData({ name: profile?.name || '', phone: profile?.phone || '' }); }} style={{
                    padding: '12px 20px', backgroundColor: COLORS.background, color: COLORS.text,
                    border: `1px solid ${COLORS.border}`, borderRadius: '10px', cursor: 'pointer', fontWeight: '600',
                  }}>
                    {t('cancel')}
                  </button>
                </div>
              ) : (
                <button onClick={() => setEditMode(true)} style={{
                  padding: '12px 20px', backgroundColor: COLORS.background, color: COLORS.text,
                  border: `1px solid ${COLORS.border}`, borderRadius: '10px', cursor: 'pointer',
                  fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
                }}>
                  <Edit3 size={18} /> {t('editProfile')}
                </button>
              )}
            </div>

            {/* Balance & Stats */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '24px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                  <span style={{ color: COLORS.textSecondary, fontSize: '14px' }}>{t('totalBalance')}</span>
                  <button onClick={() => setShowBalance(!showBalance)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                    {showBalance ? <Eye size={16} color={COLORS.textSecondary} /> : <EyeOff size={16} color={COLORS.textSecondary} />}
                  </button>
                </div>
                <div style={{ fontSize: '38px', fontWeight: '700', color: COLORS.text }}>
                  {formatCurrency(balance?.exchange_balance)}
                </div>
              </div>
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', borderRadius: '24px',
                backgroundColor: stats.totalPnL >= 0 ? `${COLORS.success}15` : `${COLORS.danger}15`,
              }}>
                {stats.totalPnL >= 0 ? <TrendingUp size={18} color={COLORS.success} /> : <TrendingDown size={18} color={COLORS.danger} />}
                <span style={{ color: stats.totalPnL >= 0 ? COLORS.success : COLORS.danger, fontWeight: '600', fontSize: '15px' }}>
                  {showBalance ? `${stats.totalPnL >= 0 ? '+' : ''}$${stats.totalPnL.toFixed(2)}` : '••••'}
                </span>
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
              {[
                { label: t('winRate'), value: `${stats.winRate.toFixed(1)}%`, color: COLORS.primary },
                { label: t('trades'), value: stats.totalTrades, color: COLORS.text },
                { label: t('profitable'), value: stats.winTrades, color: COLORS.success },
                { label: t('losing'), value: stats.lossTrades, color: COLORS.danger },
                { label: t('open'), value: stats.openTrades, color: COLORS.warning },
              ].map((stat, i) => (
                <div key={i} style={{
                  textAlign: 'center', padding: '18px 12px', backgroundColor: COLORS.background, borderRadius: '14px',
                }}>
                  <div style={{ fontSize: '26px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '12px', color: COLORS.textSecondary, marginTop: '4px' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Connected Wallets */}
          <div style={{
            backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '20px', padding: '24px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Wallet size={22} color={COLORS.primary} />
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text, margin: 0 }}>{t('connectedWallets')}</h3>
              </div>
              <button onClick={() => setShowWalletModal(true)} style={{
                padding: '10px 18px', backgroundColor: COLORS.primary, color: '#000', border: 'none',
                borderRadius: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <Plus size={16} /> {t('connectWallet')}
              </button>
            </div>
            {connectedWallets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: COLORS.textSecondary }}>
                {t('noWallets')}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {connectedWallets.map((wallet, idx) => (
                  <div key={idx} style={{
                    backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`,
                    borderRadius: '14px', padding: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{
                        width: '48px', height: '48px', borderRadius: '12px', backgroundColor: `${COLORS.success}20`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Wallet size={24} color={COLORS.success} />
                      </div>
                      <div>
                        <div style={{ color: COLORS.text, fontSize: '15px', fontWeight: '600', marginBottom: '4px' }}>
                          {formatAddress(wallet.address)}
                        </div>
                        <div style={{ color: COLORS.textSecondary, fontSize: '12px' }}>{wallet.walletType || 'Ethereum'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button onClick={() => handleCopy(wallet.address)} style={{
                        padding: '10px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`,
                        borderRadius: '10px', cursor: 'pointer',
                      }}>
                        {copied ? <CheckCircle size={18} color={COLORS.success} /> : <Copy size={18} color={COLORS.text} />}
                      </button>
                      <button onClick={() => window.open(`https://etherscan.io/address/${wallet.address}`, '_blank')} style={{
                        padding: '10px', backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`,
                        borderRadius: '10px', cursor: 'pointer',
                      }}>
                        <ExternalLink size={18} color={COLORS.text} />
                      </button>
                      <button onClick={() => disconnect(wallet.address)} style={{
                        padding: '10px', backgroundColor: `${COLORS.danger}15`, border: 'none',
                        borderRadius: '10px', cursor: 'pointer',
                      }}>
                        <Trash2 size={18} color={COLORS.danger} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Settings */}
          <div style={{
            backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '20px', padding: '24px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <Settings size={22} color={COLORS.primary} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: COLORS.text, margin: 0 }}>{t('settings')}</h3>
            </div>

            {/* Language */}
            <div>
              <div style={{ color: COLORS.textSecondary, fontSize: '12px', marginBottom: '10px', fontWeight: '600' }}>{t('language')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {Object.entries(LANGUAGES).map(([key, langData]) => (
                  <button
                    key={key}
                    onClick={() => setLanguage(key)}
                    style={{
                      padding: '12px 14px', backgroundColor: language === key ? `${COLORS.primary}20` : COLORS.background,
                      border: `2px solid ${language === key ? COLORS.primary : COLORS.border}`, borderRadius: '10px',
                      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                      fontSize: '13px', fontWeight: '500', color: COLORS.text,
                    }}
                  >
                    <span style={{ fontSize: '18px' }}>{langData.flag}</span>
                    {langData.label}
                    {language === key && <Check size={16} color={COLORS.primary} style={{ marginLeft: 'auto' }} />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div style={{
            backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: '20px', padding: '24px',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { icon: Shield, label: t('security'), color: COLORS.success, onClick: () => navigate('/security') },
                { icon: MessageSquare, label: t('support'), color: COLORS.warning, onClick: () => navigate('/support') },
              ].map((item, idx) => (
                <button
                  key={idx}
                  onClick={item.onClick}
                  style={{
                    width: '100%', padding: '16px', backgroundColor: COLORS.background, border: `1px solid ${COLORS.border}`,
                    borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer',
                  }}
                >
                  <item.icon size={20} color={item.color} />
                  <span style={{ flex: 1, textAlign: 'left', fontSize: '14px', color: COLORS.text }}>{item.label}</span>
                  <ChevronRight size={18} color={COLORS.textSecondary} />
                </button>
              ))}
            </div>
          </div>

          {/* Logout */}
          <button onClick={handleLogout} style={{
            width: '100%', padding: '16px', backgroundColor: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}`,
            borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', cursor: 'pointer',
          }}>
            <LogOut size={20} color={COLORS.danger} />
            <span style={{ fontSize: '15px', fontWeight: '600', color: COLORS.danger }}>{t('logout')}</span>
          </button>
        </div>
      </div>

      {/* Settings Modal for mobile fallback */}
      {showSettings && <SettingsModal />}
      
      {/* Wallet Connect Modal */}
      <WalletConnectModal 
        isOpen={showWalletModal} 
        onClose={() => setShowWalletModal(false)}
        onConnect={connect}
        connectedWallets={connectedWallets}
      />
    </div>
  );
};

export default ProfilePage;
